<script lang="ts">
	import { format } from 'svelte-i18n';
	// Stores
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import {
		consumption_device,
		consumption_rooms,
		consumption_by_device_account,
	} from 'src/stores/features/consumption';
	// Helpers
	import {
		capFirstChar,
		consumption_add_total,
		convert,
		convertValueAndUnit,
	} from '../../helpers/utilities';
	import { load_image_from } from '../../helpers/interface_helpers';
	import { devices_config } from '@devices/index';

	//Design system components
	import {
		Toolbar,
		Breadcrumbs,
		Text,
		ControlsContainer,
		Stack,
		ListItem,
		LabelList,
		LabelListV2,
		CardGrid,
		Scroll,
	} from '@Shelly/shelly-design-system';
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	// Components
	import RoomsCurrentChart from './RoomsCurrentChart.svelte';
	import RealTimeConsumptionGauge from './TotalCurrentConsumption.svelte';
	import RealTimeConsumptionByUsage from './CurrentConsumptionByUsage.svelte';
	import EmptyList from '../Lists/EmptyList.svelte';
	// Types
	import type { Device } from '../../types/Device';
	import { navigateToShop } from '../Lists/list-helpers';
	import EmptyPlaceholder from '../EmptyPlaceholder.svelte';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	// export let params = { type: '', id: '-1' };

	type ScopeType = 'room' | 'account' | 'by_device_account';
	let units = 'W';

	const _filter = (e: any, scope: ScopeType, roomID: string | number) => {
		if (scope === 'room') {
			return e.room_id === roomID;
		} else {
			return e;
		}
	};

	const getConsumptionData = (
		stores: any,
		roomID: string | number = '',
		scope: ScopeType = 'room',
	) => {
		return Object.values(stores[scope].all)
			.filter((e) => _filter(e, scope, roomID))
			.reduce(
				(
					filtered,
					v: {
						id: number;
						name: string;
						image: string;
						backgroundColor: AvatarColorVariantTypes;
						icon: string;
					},
				) => {
					if (typeof stores[scope].power_data[v.id] !== 'undefined') {
						filtered[v.id] = {
							...stores[scope].power_data[v.id],
							title: v.name,
							id: v.id,
							position: stores[scope].all[v.id].position,
							image: v.image ? load_image_from(v.image) : undefined,
							backgroundColor: v.backgroundColor,
							icon: v.icon,
						};
					} else if (scope === 'account') {
						filtered[v.id] = {
							in_power: 0,
							ex_power: 0,
							_power: 0,
							title: v.name,
							id: v.id,
							position: stores[scope].all[v.id].position,
						};
					}
					return filtered;
				},
				{},
			);
	};

	const onDeviceClick = (device) => {
		setSidePanelState({
			type: 'device',
			id: device.id,
			open: true,
		});
	};

	const getDeviceConsumption = (device: any) => {
		const isDevicePower = device._power !== 0 && !!device._power;
		const devicePower = !isDevicePower ? device.in_power || device.ex_power : device._power;
		return convertValueAndUnit(devicePower, units);
	};

	const checkIsDeviceConsumption = (device: any) => {
		return (device._power !== 0 && !!device._power) || device.in_power || device.ex_power;
	};

	$: stores = {
		account: { all: $rooms, power_data: $consumption_rooms },
		room: { all: $devices, power_data: $consumption_device },
		by_device_account: {
			all: $devices,
			power_data: $consumption_by_device_account,
		},
	};

	$: roomsCurrentConsList = getConsumptionData(stores, '', 'account');
	$: accountConsumptionByDevices = getConsumptionData(stores, '', 'by_device_account');

	$: roomsWithConsumption = Object.values($rooms)
		.map((room: { name: string; id: string | number }) => {
			const roomDevices = Object.values(getConsumptionData(stores, room.id, 'room'));
			const inRoomPowerTotal = consumption_add_total(roomDevices, 'in_power');
			const eXPowerTotal = consumption_add_total(roomDevices, 'ex_power');
			const totalInOutRoomConsumption = convert(inRoomPowerTotal, units);
			const totalRoomConsumption = convertValueAndUnit(inRoomPowerTotal + eXPowerTotal, units);
			const isDeviceWithConsump = roomDevices.findIndex(checkIsDeviceConsumption);

			if (isDeviceWithConsump !== -1) {
				const filteredRoomDevices = roomDevices
					.filter(checkIsDeviceConsumption)
					.sort((a: any, b: any) => {
						// order devices by consumption
						return b._power - a._power;
					});

				return {
					...room,
					totalInOutRoomConsumption,
					totalRoomConsum: totalRoomConsumption.value,
					devices: filteredRoomDevices,
				};
			}
		})
		.filter((r) => r);

	// Get all devices in acc that are able to make consumption
	$: consumableDevices = Object.values($devices).filter(
		(device: Device) => devices_config[device.type]?.isPM && !device.shared,
	);

	let gridColsCount;
</script>

<Toolbar class="mb-m">
	<Breadcrumbs
		aria-label={$format('breadcrumbs_energy_real_time', { default: 'Energy real time' })}
		slot="breadcrumbs"
		links={[{ label: $format('tab_energy_item_real_time', { default: 'Real time' }) }]}
	/>
</Toolbar>

<!-- {#if Object.values($devices).length === 0}
	<EmptyDeviceList />
	{/if} -->
{#if consumableDevices.length === 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('no_power_measuring_device_title', {
				default: 'No power measuring devices.',
			})}
			description={$format('get_necessary_device_to_track_usage', {
				default: 'Get the necessary devices to start tracking energy usage.',
			})}
			btnText={capFirstChar(
				$format('button_go_to_store', {
					default: 'Go to store',
				}).toLocaleLowerCase(),
			)}
			onClick={() => {
				navigateToShop();
			}}
		/>
	</div>
{:else}
	<CardGrid lg={350} sm={270} class="mt-m" calcGridCols bind:gridColsCount>
		<RealTimeConsumptionGauge {accountConsumptionByDevices} />
		<!-- {#if Object.values($consumption_by_device_account).length > 0} -->
		<RealTimeConsumptionByUsage
			bind:gridColsCount
			devicesCurrentConsList={$consumption_by_device_account}
		/>
		<!-- {/if} -->
		{#if roomsWithConsumption.length > 0 && gridColsCount >= 4}
			<div class="extended-grid-column">
				<RoomsCurrentChart {roomsCurrentConsList} />
			</div>
		{/if}
	</CardGrid>
	{#if roomsWithConsumption.length > 0 && gridColsCount < 4}
		<RoomsCurrentChart {roomsCurrentConsList} />
	{/if}
	<CardGrid lg={350} sm={270} class="mt-md-m mt-sm-s">
		{#each roomsWithConsumption as room}
			<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
				<svelte:fragment slot="first">
					<Text class="text-bold mb-s">{room.name}</Text>
					<div class="d-flex justify-content-center">
						{#if $showNewLayout}
							<LabelListV2
								class="mb-s"
								size="m"
								labelList={[
									{
										icon: 'fal fa-bolt icon-ai-color',
										value: `${room.totalInOutRoomConsumption}`,
									},
								]}
								a11yProps={{ isInteractive: true }}
							/>
						{:else}
							<LabelList
								class="mb-s"
								size="xl"
								labelList={[
									{
										icon: 'fas fa-bolt',
										value: `${room.totalInOutRoomConsumption}`,
									},
								]}
							/>
						{/if}
					</div>
					<Scroll style="height:auto; max-height:270px">
						<Stack gap="s">
							{#each room.devices as device}
								{@const deviceConsumption = getDeviceConsumption(device)}
								<ListItem
									uniqueId={device.id}
									title={device.title}
									image={device.image}
									backgroundColor={device.backgroundColor}
									icon={device.icon}
									progress={{
										percent:
											(Math.abs(deviceConsumption.value) /
												Math.abs(room.totalRoomConsum)) *
											100,
										title: `${deviceConsumption.value} ${deviceConsumption.unit}`,
										color:
											device.in_power !== 0
												? device.in_power > 0
													? 'primary'
													: 'solar'
												: 'white',
										showPercentBar: true,
										...(device.in_power !== 0 &&
											device.in_power > 0 && {
												progressColor: 'gradient-chart-consumed-energy',
											}),
									}}
									actionBtn
									on:click={() => {
										onDeviceClick(device);
									}}
								/>
							{/each}
						</Stack>
					</Scroll>
				</svelte:fragment>
			</ControlsContainer>
		{/each}
	</CardGrid>
{/if}

<style>
	.extended-grid-column {
		display: grid;
		grid-column: 3 / -1;
	}
</style>
